import { useCallback } from 'react';
import {
  createInstitution,
  deleteInstitution,
  updateInstitution,
} from '../api';
import { InstitutionRequest } from '../interfaces/type';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../locales/translations';

export interface UseActions {
  create: (data: InstitutionRequest) => Promise<void>;
  update: (
    institutionId: string | number,
    data: InstitutionRequest,
  ) => Promise<void>;
  delete: (institutionId: string | number) => Promise<void>;
}
type ActionType = 'create' | 'update' | 'delete';

export default function useActions(props: {
  onSuccess: (action: ActionType) => void;
  onError: (action: ActionType, message?: string) => void;
}) {
  const { t } = useTranslation(TranslationsKeys.CONFIGURATION_INSTITUTIONS);
  const { onSuccess, onError } = props;

  const createAction = useCallback(
    async (data: InstitutionRequest) => {
      const result = await createInstitution(data);
      if (!result.error) {
        return onSuccess('create');
      }
      if (['DUPLICATED_CODE'].includes(result.error?.data?.message)) {
        return onError('create', t(`errors.${result.error?.data?.message}`));
      }
      onError('create');
    },
    [onSuccess, onError, t],
  );

  const updateAction = useCallback(
    async (institutionId: string | number, data: InstitutionRequest) => {
      const result = await updateInstitution(institutionId, data);
      if (!result.error) {
        return onSuccess('update');
      }
      if (['DUPLICATED_CODE'].includes(result.error?.data?.message)) {
        return onError('update', t(`errors.${result.error?.data?.message}`));
      }
      onError('update');
    },
    [onSuccess, onError, t],
  );

  const deleteAction = useCallback(
    async (institutionId: string | number) => {
      const result = await deleteInstitution(institutionId);
      if (!result.error) {
        return onSuccess('delete');
      }
      if (['RELATIONS_RESTRICTION'].includes(result.error?.data?.message)) {
        return onError('delete', t(`errors.${result.error?.data?.message}`));
      }
      onError('delete');
    },
    [onSuccess, onError, t],
  );

  return {
    create: createAction,
    update: updateAction,
    destroy: deleteAction,
  };
}
